import React, { createContext, useCallback, useContext, useState } from "react";

import { useParams } from "react-router-dom";

import { PersonId } from "@fartherfinance/frontend/api/Investment/types";
import useGrantAccessToResource from "@fartherfinance/frontend/api/Sharing/hooks/useGrantAccessToResource";
import useRemoveAccessToResource from "@fartherfinance/frontend/api/Sharing/hooks/useRemoveAccessToResource";
import useShareResourceToPerson from "@fartherfinance/frontend/api/Sharing/hooks/useShareResourceToPerson";
import useUnshareResourceToPerson from "@fartherfinance/frontend/api/Sharing/hooks/useUnshareResourceToPerson";
import {
  Organization,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import {
  typedEntries,
  typedKeys,
} from "@src/multiCustodian/utils/arrayHelpers";
import { pluralize } from "@src/utils/pluralize";

type AdvisorStorage = { [key: PersonId]: boolean };
type OrgStorage = { [key in Organization]?: boolean };

type AccessContextType = {
  resetState: () => void;
  onUpdate: () => void;
  advisorHash: AdvisorStorage;
  organizationsHash: OrgStorage;
  advisorCheckBox: (advisorId: PersonId, checked: boolean) => () => void;
  organizationSwitch: (
    organization: Organization,
    organizationHasAccess: boolean,
    advisorsData: { personId: PersonId; allowed: boolean }[]
  ) => (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => Promise<void>;
  isMutating: boolean;
  selectedAccordion: Organization | undefined;
  onSelectedAccordion: (organization: Organization) => void;
};

const AccessContext = createContext<AccessContextType | undefined>(undefined);

const useAccessContext = () => {
  const context = useContext(AccessContext);
  if (context === undefined) {
    throw new Error(
      "useAccessContext must be used within the AccessContextProvider"
    );
  }
  return context;
};

const AccessContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedAccordion, setSelectedAccordion] = useState<
    Organization | undefined
  >(undefined);

  const onSelectedAccordion = (organization: Organization) => {
    if (selectedAccordion === organization) {
      setSelectedAccordion(undefined);
      return;
    }
    setSelectedAccordion(organization);
  };

  const [organizationsHash, setOrganizationsHash] = useState<OrgStorage>({});

  const organizationSwitch =
    (
      organization: Organization,
      organizationHasAccess: boolean,
      advisorsData: { personId: PersonId; allowed: boolean }[]
    ) =>
    async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();

      if (organizationHasAccess === true) {
        setSelectedAccordion(undefined);

        const newAdvisorHash = typedKeys(advisorHash)
          .filter(
            (advisorId) =>
              !advisorsData.some((advisor) => advisor.personId === advisorId)
          )
          .reduce<AdvisorStorage>(
            (acc, advisorId) => ({
              ...acc,
              [advisorId]: advisorHash[advisorId as PersonId],
            }),
            {}
          );

        setAdvisorHash(newAdvisorHash);
      } else {
        setSelectedAccordion(organization);
      }

      // if the organization was never set, set it here
      if (organizationsHash[organization] === undefined) {
        setOrganizationsHash({
          ...organizationsHash,
          [organization]: !organizationHasAccess,
        });

        return;
      }

      const { [organization]: value, ...rest } = organizationsHash;
      setOrganizationsHash(rest);
    };

  const [advisorHash, setAdvisorHash] = useState<AdvisorStorage>({});

  const advisorCheckBox = (advisorId: PersonId, checked: boolean) => () => {
    if (advisorHash[advisorId] === undefined) {
      setAdvisorHash({
        ...advisorHash,
        [advisorId]: !checked,
      });
      return;
    }

    const { [advisorId]: value, ...rest } = advisorHash;
    setAdvisorHash(rest);
  };

  const { portfolioPlanId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
  }>();

  const resetState = () => {
    setOrganizationsHash({});
    setAdvisorHash({});
  };

  const auth = useAdvisorRequestAuth();

  const { mutateAsync: unshareOrganization, isLoading: unshareOrgIsLoading } =
    useRemoveAccessToResource(auth, {
      resourceId: portfolioPlanId,
      resourceType: "PortfolioPlan",
    });

  const { mutateAsync: shareOrganization, isLoading: shareOrgIsLoading } =
    useGrantAccessToResource(auth, {
      resourceId: portfolioPlanId,
      resourceType: "PortfolioPlan",
    });

  const notification = useStatusNotification();

  const onOrganizationUpdate = useCallback(async () => {
    const orgsChanges = typedEntries(organizationsHash);
    if (orgsChanges.length === 0) {
      return;
    }

    const orgsPromises = orgsChanges.map(([key, value]) => {
      if (value === true) {
        return shareOrganization(key);
      }
      return unshareOrganization(key);
    });

    const results = await Promise.allSettled(orgsPromises);
    const successCount = results.filter(
      (result) => result.status === "fulfilled"
    ).length;
    const failureCount = results.length - successCount;

    if (failureCount === 0) {
      return notification(
        `Successfully updated ${successCount} ${pluralize(
          successCount,
          "organization"
        )} access`,
        "Success"
      );
    }

    if (successCount === 0) {
      return notification(
        `All ${failureCount} organization ${pluralize(
          failureCount,
          "changes"
        )} failed`,
        "Error"
      );
    }

    notification(
      `${successCount} organization ${pluralize(
        successCount,
        "changes"
      )} succeeded, but ${failureCount} failed.`,
      "Error"
    );
  }, [notification, organizationsHash, shareOrganization, unshareOrganization]);

  const { mutateAsync: unsharePerson, isLoading: unsharePersonIsLoading } =
    useUnshareResourceToPerson(auth, {
      resourceId: portfolioPlanId,
      resourceType: "PortfolioPlan",
    });

  const { mutateAsync: sharePerson, isLoading: shareParsonIsLoading } =
    useShareResourceToPerson(auth, {
      resourceId: portfolioPlanId,
      resourceType: "PortfolioPlan",
    });

  const onAdvisorUpdate = useCallback(async () => {
    const advisorChanges = typedEntries(advisorHash);
    if (advisorChanges.length === 0) {
      return;
    }

    const advisorPromises = advisorChanges.map(([key, value]) => {
      if (value === true) {
        return sharePerson(key);
      }
      return unsharePerson(key);
    });

    const results = await Promise.allSettled(advisorPromises);
    const successCount = results.filter(
      (result) => result.status === "fulfilled"
    ).length;
    const failureCount = results.length - successCount;

    if (failureCount === 0) {
      return notification(
        `Successfully updated ${successCount} ${pluralize(
          successCount,
          "advisor"
        )} access`,
        "Success"
      );
    }

    if (successCount === 0) {
      return notification(
        `All ${failureCount} advisor ${pluralize(
          failureCount,
          "changes"
        )}  failed`,
        "Error"
      );
    }

    notification(
      `${successCount} advisor ${pluralize(
        successCount,
        "changes"
      )} succeeded, but ${failureCount} failed.`,
      "Error"
    );
  }, [advisorHash, notification, sharePerson, unsharePerson]);

  const onUpdate = useCallback(async () => {
    await onAdvisorUpdate();
    await onOrganizationUpdate();

    // clear state
    setOrganizationsHash({});
    setAdvisorHash({});
  }, [onAdvisorUpdate, onOrganizationUpdate]);

  return (
    <AccessContext.Provider
      value={{
        organizationsHash,
        organizationSwitch,
        advisorHash,
        advisorCheckBox,
        resetState,
        onUpdate,
        selectedAccordion,
        onSelectedAccordion,
        isMutating:
          unsharePersonIsLoading ||
          unshareOrgIsLoading ||
          shareOrgIsLoading ||
          shareParsonIsLoading,
      }}
    >
      {children}
    </AccessContext.Provider>
  );
};

export { useAccessContext };
export default AccessContextProvider;
